import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography, makeStyles, Modal, Backdrop } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import IconButton from '@material-ui/core/IconButton';
import { useState } from 'react';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { formatCurrency } from '@roc/ui/utils';
import { BackgroundItemStore } from '../stores/backgroundItemStore';
import NewTradelineDataItemForm from './newTradelineDataItemForm';
import { TRADELINE_DATA_RECORD, getInfoButton, getResolutionFlag } from './utils/backgroundUtils';

const modalStyles = makeStyles((theme: Theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '10px',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    minWidth: '400px',
  },
}));

const useStyles = makeStyles((theme: Theme) => ({
  fixedWidthText: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    paddingLeft: '10px',
    width: '100%'
  },
  ssnCell: {
    textAlign: 'center',
    minWidth: '120px',
  },
  tableContainer: {
    borderRadius: '10px',
    border: '1px #e0e0e0 solid',
    overflow: 'hidden',
  },
  table: {
    minWidth: 750,
    width: '100%',
    '& td': {
      height: '40px',
      padding: 0,
      paddingTop: '8px',
      paddingBottom: '8px',
      border: '1px solid #e0e0e0',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
  },

  editableRow: {
    position: 'relative',
    cursor: 'pointer',
    textAlign: 'center',
    '& td': {
      textAlign: 'center'
    },
  },

  headerCell: {
    height: '41px',
    fontWeight: 'bold',
    color: '#757575',
    padding: 0,
    backgroundColor: '#F0FAFF',
    textAlign: 'center',
    border: '1px solid #e0e0e0',
  },

  titleCell: {
    height: '46px',
    padding: 0,
    overflow: 'hidden',
    backgroundColor: '#E2F6FF'
  },

  descriptionCell: {
    whiteSpace: 'pre-wrap',
  },

  inputBase: {
    width: '100%',
    '& input': {
      backgroundColor: '#fdefbf',
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      fontSize: 14,
      color: 'black',
      fontWeight: theme.typography.fontWeightBold,
      padding: '8px 4px',
      cursor: 'pointer',
      '&:focus': {
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  autocompletePaper: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    fontSize: 13,
  },
  autocompleteOption: {
    minHeight: 'auto',
    alignItems: 'flex-start',
    padding: 8,
    '&[data-focus="true"]': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  addNewItemCell: {
    padding: 0,
    height: '40px',
    backgroundColor: '#fdefbf',
  },
  addNewItemRow: {
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
  rowActions: {
    visibility: 'hidden',
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 99,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      visibility: 'visible',
    },
  },
  horizontalScroll: {
    [theme.breakpoints.down('md')]: {
      overflowX: 'scroll',
    },
  },
  clickHereToEdit: {
    color: theme.palette.grey[400],
    fontSize: theme.typography.fontSize * 0.8,
  },
  clickHereToEditHidden: {
    visibility: 'hidden',
  },
  title: {
    color: '#757575',
    fontSize: 20
  },
}));

interface TradelineDataProps {
  store: BackgroundItemStore;
  openResolutionInfo?: (data?: any) => void;
}

const TradelineData = observer(({ store, openResolutionInfo }: TradelineDataProps) => {
  const classes = useStyles();
  const columns = [
    {
      name: 'Item',
      width: '19%',
    },
    {
      name: 'Outstanding #',
      width: '9%',
    },
    {
      name: 'Paid #',
      width: '9%',
    },
    {
      name: 'Outstanding Amount',
      width: '12%',
    },
    {
      name: 'Paid Amount',
      width: '12%',
    },
    {
      name: 'Past Due',
      width: '12%',
    },
    {
      name: 'Resolution Flag',
      width: '15%',
    },
    ...(store.isInternal
      ? [
        {
          name: 'ACTIONS',
          width: '15%',
        },
      ]
      : []),
  ];

  const updateRecordItem = (index: number) => {
    store.updateRecordItem(index, TRADELINE_DATA_RECORD);
  };

  const removeRecordItem = (index: number) => {
    store.removeTDRecordItem(index, TRADELINE_DATA_RECORD);
  };

  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [indexToEdit, setIndexToEdit] = useState(null);
  const modalClasses = modalStyles();
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenEdit = (index: number) => {
    setIndexToEdit(index);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  interface AddNewItemModal {
    store: BackgroundItemStore;
  }

  interface EditItemModal {
    store: BackgroundItemStore;
  }

  const NewItemModal = observer(({ store }: AddNewItemModal) => {
    return (
      <Modal
        className={modalClasses.modal}
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 100,
        }}
      >
        <div className={modalClasses.paper}>
          <NewTradelineDataItemForm store={store} handleClose={handleClose} subtitle={store.itemsMap[TRADELINE_DATA_RECORD].name} record={TRADELINE_DATA_RECORD} />
        </div>
      </Modal>
    );
  });

  const EditItemModal = observer(({ store }: EditItemModal) => {
    return (
      <Modal
        className={modalClasses.modal}
        open={openEdit}
        onClose={handleCloseEdit}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 100,
        }}
      >
        <div className={modalClasses.paper}>
          <NewTradelineDataItemForm store={store} handleClose={handleCloseEdit} subtitle={store.itemsMap[TRADELINE_DATA_RECORD].name} index={indexToEdit} record={TRADELINE_DATA_RECORD} />
        </div>
      </Modal>
    );
  });

  return (
    <Box style={{ paddingTop: '15px' }}>
      {<NewItemModal store={store} />}
      {<EditItemModal store={store} />}
      {store.isInternal &&
        <Box style={{ display: 'flex', justifyContent: 'right', marginBottom: '10px' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpen}
            disabled={false}
            startIcon={<AddCircleOutlineIcon />}
            style={{ color: '#2199E0' }}
          >
            ADD NEW ITEM
          </Button>
        </Box>
      }
      <div className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead><TableRow>
            <TableCell colSpan={columns.length} className={classes.titleCell}>
              <Typography className={classes.title}>Tradeline Data</Typography>
            </TableCell>
          </TableRow>
            <TableRow>
              {columns.map(({ name, ...props }) => (
                <TableCell key={name} className={classes.headerCell} style={{ paddingLeft: '8px' }}>
                  {name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className={classes.editableRow}>
            {store.tradelineDataRows.map((q, index) => {
              return (
                <TableRow key={index}>
                  <TableCell width={columns[0].width}>
                    <span className={classes.fixedWidthText}>{q.item}</span>
                  </TableCell>
                  <TableCell width={columns[1].width}>
                    <span className={classes.fixedWidthText}>{q.outstandingCount}</span>
                  </TableCell>
                  <TableCell width={columns[2].width}>
                    <span className={classes.fixedWidthText}>{q.paidCount}</span>
                  </TableCell>
                  <TableCell width={columns[3].width}>
                    <span className={classes.fixedWidthText}>{formatCurrency(q.balanceAmount || '0')}</span>
                  </TableCell>
                  <TableCell width={columns[4].width}>
                    <span className={classes.fixedWidthText}>{formatCurrency(q.highCreditAmount || '0')}</span>
                  </TableCell>
                  <TableCell width={columns[5].width}>
                    <span className={classes.fixedWidthText}>{formatCurrency(q.pastDue || '0')}</span>
                  </TableCell>

                  <TableCell width={columns[6].width}>
                    <IconButton onClick={() => {
                      updateRecordItem(index);
                    }}>
                      {getResolutionFlag(q)}
                    </IconButton>
                    {getInfoButton(q, () => openResolutionInfo(q))}
                  </TableCell>

                  {store.isInternal &&
                    <TableCell width={columns[7].width}>
                      <IconButton onClick={() => { handleOpenEdit(index) }}>
                        <EditIcon style={{ color: '#2199E0' }} />
                      </IconButton>
                      <IconButton onClick={() => { removeRecordItem(index) }}>
                        <DeleteIcon style={{ color: '#2199E0' }} />
                      </IconButton>
                    </TableCell>
                  }
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell width={columns[0].width}>
                <span className={classes.fixedWidthText}>
                  {store.tradelineTotalRow.item}
                </span>
              </TableCell>
              <TableCell width={columns[1].width}>
                <span className={classes.fixedWidthText}>
                  {store.tradelineTotalRow.outstandingCount}
                </span>
              </TableCell>
              <TableCell width={columns[2].width}>
                <span className={classes.fixedWidthText}>
                  {store.tradelineTotalRow.paidCount}
                </span>
              </TableCell>
              <TableCell width={columns[3].width}>
                <span className={classes.fixedWidthText}>
                  {formatCurrency(store.tradelineTotalRow.balanceAmount || '0')}
                </span>
              </TableCell>
              <TableCell width={columns[4].width}>
                <span className={classes.fixedWidthText}>
                  {formatCurrency(
                    store.tradelineTotalRow.highCreditAmount || '0'
                  )}
                </span>
              </TableCell>
              <TableCell width={columns[5].width}>
                <span className={classes.fixedWidthText}>
                  {formatCurrency(store.tradelineTotalRow.pastDue || '0')}
                </span>
              </TableCell>
              {store.isInternal &&
                <TableCell width={columns[6].width}>
                  <div style={{ height: '48px' }} />
                </TableCell>
              }
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Box>
  );
});

export default TradelineData;